import React, { useMemo, ReactNode } from 'react'
import DataTable from 'react-data-table-component'

// Column schema definition
export interface ColumnSchema {
    key: string
    name: string
    type: 'text' | 'number' | 'date' | 'status' | 'custom' | 'boolean'
    sortable?: boolean
    width?: string
    minWidth?: string
    maxWidth?: string
    customRenderer?: (value: any, row: any, index: number) => React.ReactNode
    selector?: (row: any) => any
}

// Props interface for the component
export interface DynamicSpreadsheetTableProps {
    rows: any[]
    columns: ColumnSchema[]
    title?: string
    primaryHeaders?: Array<{
        name: string | ReactNode
        startColumn: number
        endColumn: number
        style?: React.CSSProperties
    }>
    dense?: boolean
    highlightOnHover?: boolean
    pagination?: boolean
    customStyles?: any
    onSort?: (key: string) => void
    sortConfig?: {
        key: string
        direction: 'asc' | 'desc'
    } | null
}

export const SchedulerSpreadsheet = ({
    rows,
    columns,
    title,
    primaryHeaders,
    dense = false,
    highlightOnHover = false,
    pagination = false,
    customStyles,
    onSort,
    sortConfig,
}: DynamicSpreadsheetTableProps) => {
    // Convert ColumnSchema to TableColumn for react-data-table-component
    const tableColumns = useMemo(() => {
        return columns.map((col, index) => ({
            name: col.name,
            selector: col.selector || ((row: any) => row[col.key] || ''),
            sortable: false, // Disable built-in sorting since we handle it ourselves
            width: col.width || DEFAULT_COLUMN_WIDTH, // Keep as string for DataTable
            minWidth: col.width || DEFAULT_COLUMN_WIDTH, // Keep as string for DataTable
            maxWidth: col.width || DEFAULT_COLUMN_WIDTH, // Keep as string for DataTable
            wrap: true, // Allow text wrapping in headers
            grow: 1, // Allow columns to grow
            shrink: 1, // Allow columns to shrink
            cell:
                col.type === 'custom' && col.customRenderer
                    ? (row: any) => col.customRenderer!(row[col.key], row, 0)
                    : undefined,
            ...(col.type === 'date' && {
                cell: (row: any) => {
                    const value = row[col.key]
                    if (!value) return ''
                    try {
                        return new Date(value).toLocaleDateString()
                    } catch {
                        return value
                    }
                },
            }),
            ...(col.type === 'status' && {
                cell: (row: any) => {
                    const value = row[col.key]
                    if (!value) return ''
                    return (
                        <span
                            style={{
                                padding: '4px 8px',
                                borderRadius: '12px',
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                fontSize: '0.75rem',
                            }}
                        >
                            {value}
                        </span>
                    )
                },
            }),
            ...(col.type === 'boolean' && {
                cell: (row: any) => {
                    const value = row[col.key]
                    return value ? '✓' : '✗'
                },
            }),
            // Add sorting indicator and click handler for sortable columns
            ...(col.sortable &&
                onSort && {
                    name: (
                        <div
                            style={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                            }}
                            onClick={() => onSort(col.key)}
                        >
                            {col.name}
                            {sortConfig?.key === col.key && (
                                <span style={{ fontSize: '12px' }}>
                                    {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                </span>
                            )}
                        </div>
                    ),
                }),
        }))
    }, [columns, onSort, sortConfig])

    // Helper function to parse width values consistently
    const parseWidth = (width: string): number => {
        if (!width) return DEFAULT_COLUMN_WIDTH_NUMBER
        if (width.endsWith('px')) {
            return (
                parseInt(width.replace('px', '')) || DEFAULT_COLUMN_WIDTH_NUMBER
            )
        }
        if (width.endsWith('%')) {
            return (
                parseInt(width.replace('%', '')) || DEFAULT_COLUMN_WIDTH_NUMBER
            )
        }
        return parseInt(width) || DEFAULT_COLUMN_WIDTH_NUMBER
    }

    // Calculate total table width for proper alignment
    const totalTableWidth = useMemo(() => {
        return columns.reduce((sum, col) => {
            return sum + parseWidth(col.width || DEFAULT_COLUMN_WIDTH)
        }, 0)
    }, [columns])

    // Performance optimization: only re-render when necessary
    const memoizedRows = useMemo(() => rows, [rows])
    const memoizedColumns = useMemo(() => tableColumns, [tableColumns])

    // Default styles
    const defaultStyles = {
        table: {
            style: {
                minWidth: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.85rem',
                width: `${totalTableWidth}px`, // Set explicit table width
                tableLayout: 'fixed', // Use fixed table layout for consistent column widths
            },
        },
        headRow: {
            style: {
                position: 'sticky',
                top: '0px', // Stick to the top of the scrollable area
                zIndex: 2,
                backgroundColor: '#f5f5f5',
            },
        },
        headCells: {
            style: {
                backgroundColor: '#f5f5f5',
                fontWeight: 'bold',
                borderRight: '1px solid #e0e0e0',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                overflow: 'visible',
                width: 'auto',
                minWidth: 'fit-content',
                maxWidth: 'none',
                minHeight: 'auto',
                padding: '8px 8px 8px 12px',
                lineHeight: '1.2',
                textAlign: 'left',
                verticalAlign: 'top',
                boxSizing: 'border-box',
                wrap: true,
            },
        },
        rows: {
            style: {
                minHeight: '32px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        cells: {
            style: {
                padding: '0px', // Remove padding from cells to allow inner divs to fill entire area
                borderRight: '1px solid #e0e0e0',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                boxSizing: 'border-box',
            },
        },
    }

    return (
        <div style={{ height: 'calc(100vh - 320px)' }}>
            <style>
                {`
                    /* Sticky columns for horizontal scrolling */
                    .sticky-table .rdt_TableCol:first-child,
                    .sticky-table .rdt_TableCell:first-child {
                        position: sticky !important;
                        left: 0px !important;
                        background-color: #f5f5f5 !important;
                        z-index: 1 !important;
                    }
                    .sticky-table .rdt_TableCol:nth-child(2),
                    .sticky-table .rdt_TableCell:nth-child(2) {
                        position: sticky !important;
                        left: 120px !important;
                        background-color: #f5f5f5 !important;
                        z-index: 1 !important;
                        box-shadow: 2px 0 5px rgba(0,0,0,0.1) !important;
                    }
                    /* Ensure data cells have white background */
                    .sticky-table .rdt_TableCell:first-child {
                        background-color: white !important;
                        z-index: 1 !important;
                    }
                    .sticky-table .rdt_TableCell:nth-child(2) {
                        background-color: white !important;
                        z-index: 1 !important;
                    }
                    /* Make only the portion of primary header covering first two columns sticky */
                    .sticky-primary-header {
                        position: sticky !important;
                        left: 0px !important;
                        z-index: 2 !important;
                        background-color: #e3f2fd !important;
                        width: 240px !important; /* Exact width of first two columns */
                        max-width: 240px !important;
                    }
                `}
            </style>
            {title && (
                <div
                    style={{
                        padding: '16px',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        borderBottom: '1px solid #e0e0e0',
                    }}
                >
                    {title}
                </div>
            )}

            {/* Single scroll container for both headers and table */}
            <div style={{ overflow: 'auto', height: 'calc(100vh - 320px)' }}>
                {/* Primary Headers - Sticky at top of scroll container */}
                {primaryHeaders && primaryHeaders.length > 0 && (
                    <div
                        style={{
                            display: 'flex',
                            borderBottom: '2px solid #1976d2',
                            backgroundColor: '#f8f9fa',
                            width: `${totalTableWidth}px`,
                            flexShrink: 0,
                            position: 'sticky',
                            top: '0px',
                            zIndex: 3,
                        }}
                    >
                        {primaryHeaders.map((header, index) => {
                            // Calculate the width based on the columns it spans
                            const totalWidth = columns
                                .slice(header.startColumn, header.endColumn + 1)
                                .reduce(
                                    (sum, col) =>
                                        sum +
                                        parseWidth(
                                            col.width || DEFAULT_COLUMN_WIDTH,
                                        ),
                                    0,
                                )

                            // Check if this is the "Unit Info" header (first header)
                            const isUnitInfoHeader =
                                index === 0 && header.name === 'Unit Info'

                            return (
                                <div
                                    key={index}
                                    className={
                                        isUnitInfoHeader
                                            ? 'sticky-primary-header'
                                            : ''
                                    }
                                    style={{
                                        width: `${totalWidth}px`,
                                        padding: '12px 8px',
                                        textAlign: 'center',
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        borderRight: '1px solid #dee2e6',
                                        flexShrink: 0,
                                        boxSizing: 'border-box',
                                        ...header.style,
                                    }}
                                >
                                    {header.name}
                                </div>
                            )
                        })}
                    </div>
                )}

                {/* Data Table with sticky headers */}
                <div
                    className="sticky-table"
                    style={{
                        position: 'relative',
                        width: `${totalTableWidth}px`,
                        height: 'auto',
                    }}
                >
                    <DataTable
                        columns={memoizedColumns}
                        data={memoizedRows}
                        dense={dense}
                        highlightOnHover={highlightOnHover}
                        pagination={pagination}
                        customStyles={{
                            ...defaultStyles,
                            ...customStyles,
                        }}
                        fixedHeader={true}
                        responsive={false}
                        noTableHead={false}
                        progressPending={false}
                        persistTableHead={true}
                        selectableRows={false}
                        selectableRowSelected={() => false}
                        sortServer={true}
                        defaultSortFieldId={sortConfig?.key}
                        defaultSortAsc={sortConfig?.direction === 'asc'}
                    />
                </div>
            </div>
        </div>
    )
}

const DEFAULT_COLUMN_WIDTH = '120px'
const DEFAULT_COLUMN_WIDTH_NUMBER = 120
